import styled from '@emotion/styled';
import { Modal, Popover } from 'antd';
import { type FC, useEffect, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { t } from 'src/lib/player/i18n';
import { useStore } from 'src/lib/player/store';
import { correctedNow } from 'src/lib/player/utils/clock';
import { useIsMobile } from 'src/lib/player/utils/hooks';
import { IconSharePlatformAware } from '../../BasicComponents/Icons';
import { ShareDialog } from './ShareDialog';

/**
 * Prominent share affordance, sitting under the sidebar toggle. A popover reads naturally next to
 * the button on a desktop, but is cramped on a phone, so narrow screens get a centred modal
 * instead. Both wrap the same ShareDialog.
 */
export const ShareButton: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  function handleOpenChange(open: boolean) {
    if (open) trackEvent('share_open');
    setIsOpen(open);
  }

  /**
   * The dialog names one exact moment to share, so the cursor must not move while it is on screen.
   */
  useEffect(() => {
    if (!isOpen) return;

    const { isLive, isPlaying } = useStore.getState().timer;
    const { pauseTimer, resumeTimer, setTimerTime } = useStore.getState().actions;
    if (!isPlaying) return; // already paused — leave the viewer's state alone

    pauseTimer();

    return () => {
      // A live view catches up to the live edge, unless the viewer scrubbed away to pick a moment
      // to share.
      if (isLive && useStore.getState().timer.isLive) setTimerTime(correctedNow());
      resumeTimer();
    };
  }, [isOpen]);

  // Rendered only while open, so every opening gets a fresh dialog whose defaults are recalculated
  // from the current selection and time. `destroyOnHidden` below is what makes that true for the
  // popover: without it antd holds the closed dialog in its portal, stale and still subscribed.
  const dialog = isOpen ? <ShareDialog /> : null;

  const icon = <IconSharePlatformAware />;

  // A Modal has no trigger of its own, so on mobile the button opens it. A Popover does have one,
  // so on desktop the Popover must be the only owner of `isOpen` — adding an onClick here as well
  // would make the two updates cancel out in the same React batch and it would never open.
  if (isMobile) {
    return (
      <Container>
        <ShareButtonBox
          type="button"
          title={t('share.button')}
          aria-label={t('share.button')}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => handleOpenChange(true)}
        >
          {icon}
        </ShareButtonBox>
        <Modal
          open={isOpen}
          destroyOnHidden
          onCancel={() => handleOpenChange(false)}
          title={t('share.title')}
          aria-label={t('share.title')}
          aria-modal="true"
          footer={null}
          centered
        >
          {dialog}
        </Modal>
      </Container>
    );
  }

  return (
    <Container>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        destroyOnHidden
        trigger="click"
        placement="bottomLeft"
        title={t('share.title')}
        content={dialog}
      >
        <ShareButtonBox type="button" title={t('share.button')}>
          {icon}
        </ShareButtonBox>
      </Popover>
    </Container>
  );
};

const Container = styled.div`
  position: absolute;
  top: 70px;
  left: 10px;
  /**
   * The same 70px wide column the search button above occupies (its 10px margin plus a 50px
   * button), with the button centred in it — so both buttons share one vertical centre line even
   * though they are different sizes.
   */
  width: 70px;
  display: flex;
  justify-content: center;
  /* The overlay this sits in is pointer-events: none. Only the button itself opts back in, so the
     empty part of this column does not swallow drags meant for the map. */
  pointer-events: none;
  z-index: 100;
`;

/* A real button, so focus and Enter/Space come for free. */
const ShareButtonBox = styled.button`
  pointer-events: all;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: 42px;
  height: 42px;
  margin: 10px 0;
  border: 1px solid var(--base-color);
  border-radius: 21px;
  background: white;
  color: var(--text-color);
  cursor: pointer;
  font-size: 15px;

  &:active {
    opacity: 0.6;
  }
`;
